class InfiniteScrollManager {
  constructor() {
    this.minLoadedPage = 1;
    this.maxLoadedPage = 1;
    this.pagesLoaded = 1;
    this.isLoading = false;
    this.totalPages = 1;
    this.totalProducts = 0;
    this.currentUrl = window.location.href;
    this.observers = [];
    this.prevButton = null;
    this.prevButtonHandler = null;
    this.isDestroyed = false;

    this.init();
  }

  init() {
    const urlParams = new URLSearchParams(window.location.search);
    const currentPage = parseInt(urlParams.get('page'), 10) || 1;
    const progressElement = document.querySelector('.collection-progress');
    const infiniteScrollElement = document.querySelector('infinite-scroll[data-trigger="infinite"]');

    this.minLoadedPage = currentPage;
    this.maxLoadedPage = currentPage;
    this.pagesLoaded = 1;

    if (progressElement) {
      this.totalProducts = parseInt(progressElement.dataset.totalProducts, 10) || 0;
      this.totalPages = parseInt(progressElement.dataset.totalPages, 10) || 1;
    } else if (infiniteScrollElement?.dataset.totalPages) {
      this.totalPages = parseInt(infiniteScrollElement.dataset.totalPages, 10) || 1;
    } else {
      const paginationNav = document.querySelector('.pagination');
      if (paginationNav) {
        const lastPageLink = paginationNav.querySelector('a[href*="page="]:last-of-type');
        if (lastPageLink) {
          const match = lastPageLink.href.match(/page=(\d+)/);
          if (match) {
            this.totalPages = parseInt(match[1], 10);
          }
        }
      } else {
        this.totalPages = 999;
      }
    }

    this.setupInfiniteScroll();
    this.setupPrevButton();
    this.updateProgress();
  }

  setupInfiniteScroll() {
    const infiniteScrollElements = document.querySelectorAll('infinite-scroll[data-trigger="infinite"], .infinite-scroll-element');

    infiniteScrollElements.forEach((element) => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.isLoading) {
              this.loadNextPage(element);
            }
          });
        },
        {
          root: null,
          rootMargin: '0px 0px 350px 0px',
          threshold: 0,
        }
      );

      observer.observe(element);
      this.observers.push(observer);
    });
  }

  setupPrevButton() {
    const prevButton = document.querySelector('.js-show-prev-products');
    if (prevButton) {
      this.prevButton = prevButton;
      this.prevButtonHandler = () => {
        this.loadPrevPage();
      };
      prevButton.addEventListener('click', this.prevButtonHandler);
    }
  }

  destroy() {
    this.isDestroyed = true;
    this.observers.forEach((observer) => observer.disconnect());
    this.observers = [];

    if (this.prevButton && this.prevButtonHandler) {
      this.prevButton.removeEventListener('click', this.prevButtonHandler);
    }
  }

  getLoadedProductCount() {
    const grid = document.querySelector('#product-card-container');
    if (!grid) return 0;

    const ids = new Set();
    grid.querySelectorAll('[data-product_id]').forEach((element) => {
      const productId = element.dataset.product_id;
      if (productId) ids.add(productId);
    });

    return ids.size;
  }

  async loadNextPage(element) {
    if (this.isDestroyed || this.isLoading || this.maxLoadedPage >= this.totalPages) return;

    this.isLoading = true;
    const nextPage = this.maxLoadedPage + 1;

    try {
      let nextUrl = element.getAttribute('data-url');
      if (!nextUrl) {
        nextUrl = this.buildPageUrl(nextPage);
      }
      const response = await fetch(nextUrl);
      const html = await response.text();

      if (this.isDestroyed) return;

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newProducts = doc.querySelector('.product-card-list');

      if (newProducts && newProducts.innerHTML.trim()) {
        const currentGrid = document.querySelector('.product-card-list');
        if (currentGrid) {
          if (window.HPFixMobileGridParity) {
            window.HPFixMobileGridParity.clear(currentGrid);
          }

          const children = Array.from(newProducts.children);
          children.forEach((child) => {
            currentGrid.appendChild(child);
          });

          if (window.HPFixMobileGridParity) {
            window.HPFixMobileGridParity.run();
          }
        }

        this.maxLoadedPage = nextPage;
        this.pagesLoaded++;
        this.updateUrl(nextPage);
        this.updateProgress();

        document.dispatchEvent(new CustomEvent('infiniteScrollLoaded'));

        if (typeof initProductHover === 'function') {
          initProductHover();
        }
      }

      const newInfiniteScroll = doc.querySelector('infinite-scroll[data-trigger="infinite"]');
      if (newInfiniteScroll) {
        element.setAttribute('data-url', newInfiniteScroll.getAttribute('data-url'));
      } else {
        element.remove();
        this.markProgressComplete();
      }
    } catch (error) {
      console.error('Error loading next page:', error);
    } finally {
      this.isLoading = false;
    }
  }

  async loadPrevPage() {
    if (this.isDestroyed || this.isLoading || this.minLoadedPage <= 1) return;

    this.isLoading = true;
    const prevPage = this.minLoadedPage - 1;

    const prevButton = document.querySelector('.js-show-prev-products');
    const spinner = prevButton?.querySelector('.loading-overlay__spinner');
    const buttonText = prevButton?.querySelector('.js-button-text');

    if (spinner) spinner.classList.remove('hidden');
    if (buttonText) buttonText.style.visibility = 'hidden';
    if (prevButton) prevButton.disabled = true;

    try {
      const prevUrl = this.buildPageUrl(prevPage);
      const response = await fetch(prevUrl);
      const html = await response.text();

      if (this.isDestroyed) return;

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newProducts = doc.querySelector('.product-card-list');

      if (newProducts && newProducts.innerHTML.trim()) {
        const currentGrid = document.querySelector('.product-card-list');
        if (currentGrid) {
          if (window.HPFixMobileGridParity) {
            window.HPFixMobileGridParity.clear(currentGrid);
          }

          const children = Array.from(newProducts.children);
          children.reverse().forEach((child) => {
            currentGrid.insertBefore(child, currentGrid.firstChild);
          });

          if (window.HPFixMobileGridParity) {
            window.HPFixMobileGridParity.run();
          }
        }

        this.minLoadedPage = prevPage;
        this.pagesLoaded++;

        this.updateUrl(prevPage);
        this.updateProgress();

        document.dispatchEvent(new CustomEvent('infiniteScrollLoaded'));

        if (typeof initProductHover === 'function') {
          initProductHover();
        }

        if (prevPage === 1) {
          prevButton.closest('div').style.display = 'none';
        }
      }
    } catch (error) {
      console.error('Error loading previous page:', error);
    } finally {
      this.isLoading = false;
      if (spinner) spinner.classList.add('hidden');
      if (buttonText) buttonText.style.visibility = 'visible';
      if (prevButton) prevButton.disabled = false;
    }
  }

  buildPageUrl(pageNumber) {
    const url = new URL(window.location);
    if (pageNumber === 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', pageNumber);
    }
    return url.toString();
  }

  updateUrl(pageNumber) {
    const newUrl = this.buildPageUrl(pageNumber);
    history.replaceState(null, '', newUrl);
  }

  markProgressComplete() {
    const progressElement = document.querySelector('.collection-progress');
    if (!progressElement) return;

    progressElement.classList.add('is-complete');

    const totalProducts =
      parseInt(progressElement.dataset.totalProducts, 10) || this.totalProducts || this.getLoadedProductCount();
    const loadedProducts = Math.max(this.getLoadedProductCount(), totalProducts);
    const progressBar = progressElement.querySelector('.collection-progress__bar');
    const progressText = progressElement.querySelector('.progress-text');

    if (progressBar) {
      progressBar.style.width = '100%';
    }

    if (progressText) {
      progressText.textContent = `Toplam ${totalProducts} / ${totalProducts}`;
    }
  }

  updateProgress() {
    const progressElement = document.querySelector('.collection-progress');
    if (!progressElement) return;

    const totalProducts = parseInt(progressElement.dataset.totalProducts, 10) || this.totalProducts || 0;
    if (totalProducts === 0) return;

    const loadedProducts = Math.min(this.getLoadedProductCount(), totalProducts);
    const percentage = Math.min(100, (loadedProducts / totalProducts) * 100);
    const isComplete = loadedProducts >= totalProducts || this.maxLoadedPage >= this.totalPages;

    const progressBar = progressElement.querySelector('.collection-progress__bar');
    if (progressBar) {
      progressBar.style.width = `${percentage}%`;
    }

    const progressText = progressElement.querySelector('.progress-text');
    if (progressText) {
      progressText.textContent = `Toplam ${loadedProducts} / ${totalProducts}`;
    }

    progressElement.classList.toggle('is-complete', isComplete);
  }
}

function initInfiniteScrollManager() {
  if (window.infiniteScrollManager) {
    window.infiniteScrollManager.destroy();
    window.infiniteScrollManager = null;
  }

  const infiniteScrollEl = document.querySelector('infinite-scroll[data-trigger="infinite"]');
  const prevButton = document.querySelector('.js-show-prev-products');
  const progressElement = document.querySelector('.collection-progress');

  if (infiniteScrollEl || prevButton || progressElement) {
    window.infiniteScrollManager = new InfiniteScrollManager();
  }
}

document.addEventListener('DOMContentLoaded', initInfiniteScrollManager);
document.addEventListener('shopify:section:load', initInfiniteScrollManager);
document.addEventListener('collection:reloaded', initInfiniteScrollManager);
